import {
  useQueries,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysSite } from '../queryKeys';

import { fetchSite } from './fetchSite';
import { type Site } from './types';

/**
 * Generates the query options for the site query.
 *
 * @param siteId - The ID of the site to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSiteQueryOptions = <TData>({
  siteId,
  options,
}: {
  siteId: UUID | undefined;
  options?: Omit<UseQueryOptions<Site, Error, TData>, 'queryKey' | 'queryFn'>;
}) => ({
  enabled: Boolean(siteId),
  queryFn: async ({ signal }: { signal: AbortSignal }) =>
    fetchSite(siteId!, { signal }),
  queryKey: queryKeysSite.get(siteId ?? ''),
  staleTime: ms('60s'),
  ...options,
});

/**
 * React Query based custom hook for getting the data for a site with a given siteId.
 *
 * @param siteId - The ID of the site to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_site_site__site_uuid__get
 */
export const useQuerySite = <TSelected = Site>(
  siteId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<Site, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<Site, Error, TSelected>(
    getSiteQueryOptions<TSelected>({ siteId, options }),
  );

/**
 * React Query based custom hook for getting data for multiple sites based on a list of site IDs.
 *
 * @param siteIds - Array of site IDs to fetch.
 * @param options - Additional options for the useQueries hook.
 * @param combine - Optional function to process the results of the different queries.
 * @returns Array of query results for each site. If a combine function is provided, the results
 *          will be transformed according to that function.
 */
export const useQueriesSites = <TSelected>(
  siteIds: UUID[],
  options?: Omit<UseQueryOptions<Site>, 'queryKey' | 'queryFn'>,
  combine?: (results: Array<UseQueryResult<Site | undefined>>) => TSelected,
) =>
  useQueries({
    combine,
    queries: siteIds.map((siteId) => ({
      enabled: Boolean(siteId),
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        fetchSite(siteId, { signal }),
      queryKey: queryKeysSite.get(siteId),
      staleTime: ms('60s'),
      ...options,
    })),
  });
